import React, { useState, useEffect } from 'react';
import { ref, onValue, update, push, set } from 'firebase/database';
import { db, auth } from './firebase';

const s = {
  card: { backgroundColor: '#FFF', borderRadius: 12, border: '1px solid #E5E7EB', overflow: 'hidden', marginBottom: 16 },
  th: { padding: '12px 14px', textAlign: 'left', fontWeight: 600, color: '#555', borderBottom: '1px solid #E5E7EB', backgroundColor: '#F7F8FA', whiteSpace: 'nowrap', fontSize: 13 },
  td: { padding: '12px 14px', borderBottom: '1px solid #F3F4F6', fontSize: 13, color: '#374151', verticalAlign: 'top' },
  input: { padding: '9px 14px', borderRadius: 8, border: '1px solid #E5E7EB', fontSize: 13, backgroundColor: '#F7F8FA', outline: 'none' },
  btn: { padding: '8px 16px', backgroundColor: '#1D9E75', color: '#FFF', border: 'none', borderRadius: 8, cursor: 'pointer', fontWeight: 600, fontSize: 13 },
  badge: (bg, color) => ({ backgroundColor: bg, color, padding: '2px 8px', borderRadius: 20, fontSize: 11, fontWeight: 600, whiteSpace: 'nowrap' }),
};

function Badge({ text, bg, color }) {
  return <span style={s.badge(bg, color)}>{text}</span>;
}

// ── PROFILE DRAWER ────────────────────────────────────────────────────────────
function ProfileDrawer({ profile, type, orders, onClose, onAddNote }) {
  const [noteText, setNoteText] = useState('');
  const [notes, setNotes] = useState([]);
  const [savingNote, setSavingNote] = useState(false);
  const [activeTab, setActiveTab] = useState('overview');

  useEffect(() => {
    if (!profile) return;
    const path = `crm_notes/${type}/${profile.id}`;
    onValue(ref(db, path), snap => {
      setNotes(snap.exists()
        ? Object.entries(snap.val()).map(([k, v]) => ({ id: k, ...v })).sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt))
        : []);
    });
  }, [profile?.id]);

  if (!profile) return null;

  const myOrders = orders.filter(o =>
    type === 'customer' ? o.customerId === profile.uid || o.customerId === profile.id :
    type === 'pharmacy' ? o.items?.[0]?.pharmacyId === profile.id || o.items?.[0]?.pharmacy === profile.pharmacyName :
    o.riderId === profile.id
  );

  const totalSpent    = myOrders.filter(o => o.status === 'delivered').reduce((s, o) => s + (o.total || 0), 0);
  const delivered     = myOrders.filter(o => o.status === 'delivered').length;
  const cancelled     = myOrders.filter(o => o.status === 'cancelled').length;
  const lastOrder     = myOrders.sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt))[0];

  const saveNote = async () => {
    if (!noteText.trim()) return;
    setSavingNote(true);
    const noteRef = push(ref(db, `crm_notes/${type}/${profile.id}`));
    await set(noteRef, {
      text: noteText.trim(),
      createdAt: new Date().toISOString(),
      addedBy: auth.currentUser?.email,
    });
    setNoteText('');
    setSavingNote(false);
  };

  const getName = () =>
    type === 'customer' ? (profile.name || profile.displayName || profile.email?.split('@')[0] || 'Customer') :
    type === 'pharmacy' ? profile.pharmacyName :
    profile.name || 'Rider';

  const TABS = ['overview', 'orders', 'notes'];

  return (
    <div style={{ position: 'fixed', top: 0, right: 0, bottom: 0, width: 520, backgroundColor: '#FFF', boxShadow: '-4px 0 30px rgba(0,0,0,0.15)', zIndex: 9999, display: 'flex', flexDirection: 'column' }}>
      {/* DRAWER HEADER */}
      <div style={{ backgroundColor: type === 'customer' ? '#F59E0B' : type === 'pharmacy' ? '#1D9E75' : '#3B82F6', padding: '20px 24px', color: '#FFF' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
            <div style={{ width: 52, height: 52, borderRadius: 26, backgroundColor: 'rgba(255,255,255,0.25)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 24 }}>
              {type === 'customer' ? '👤' : type === 'pharmacy' ? '💊' : '🛵'}
            </div>
            <div>
              <p style={{ fontWeight: 700, fontSize: 18, margin: '0 0 3px' }}>{getName()}</p>
              <p style={{ fontSize: 12, opacity: 0.85, margin: 0 }}>
                {type === 'customer' ? profile.email :
                 type === 'pharmacy' ? `${profile.region || ''} · ${profile.licenseNumber || 'No license'}` :
                 profile.phone || profile.email}
              </p>
            </div>
          </div>
          <button style={{ background: 'rgba(255,255,255,0.2)', border: 'none', color: '#FFF', borderRadius: 8, padding: '6px 12px', cursor: 'pointer', fontSize: 18 }} onClick={onClose}>✕</button>
        </div>

        {/* QUICK STATS */}
        <div style={{ display: 'flex', gap: 16, marginTop: 16, flexWrap: 'wrap' }}>
          {type === 'customer' && (
            <>
              <div style={{ backgroundColor: 'rgba(255,255,255,0.15)', borderRadius: 8, padding: '8px 14px', textAlign: 'center' }}>
                <p style={{ fontSize: 18, fontWeight: 800, margin: 0 }}>{myOrders.length}</p>
                <p style={{ fontSize: 11, opacity: 0.85, margin: 0 }}>Total Orders</p>
              </div>
              <div style={{ backgroundColor: 'rgba(255,255,255,0.15)', borderRadius: 8, padding: '8px 14px', textAlign: 'center' }}>
                <p style={{ fontSize: 18, fontWeight: 800, margin: 0 }}>GH₵ {totalSpent.toFixed(0)}</p>
                <p style={{ fontSize: 11, opacity: 0.85, margin: 0 }}>Total Spent</p>
              </div>
              <div style={{ backgroundColor: 'rgba(255,255,255,0.15)', borderRadius: 8, padding: '8px 14px', textAlign: 'center' }}>
                <p style={{ fontSize: 18, fontWeight: 800, margin: 0 }}>{delivered}</p>
                <p style={{ fontSize: 11, opacity: 0.85, margin: 0 }}>Delivered</p>
              </div>
            </>
          )}
          {type === 'pharmacy' && (
            <>
              <div style={{ backgroundColor: 'rgba(255,255,255,0.15)', borderRadius: 8, padding: '8px 14px', textAlign: 'center' }}>
                <p style={{ fontSize: 18, fontWeight: 800, margin: 0 }}>{myOrders.length}</p>
                <p style={{ fontSize: 11, opacity: 0.85, margin: 0 }}>Total Orders</p>
              </div>
              <div style={{ backgroundColor: 'rgba(255,255,255,0.15)', borderRadius: 8, padding: '8px 14px', textAlign: 'center' }}>
                <p style={{ fontSize: 18, fontWeight: 800, margin: 0 }}>GH₵ {(totalSpent * 0.9).toFixed(0)}</p>
                <p style={{ fontSize: 11, opacity: 0.85, margin: 0 }}>Earned</p>
              </div>
              <div style={{ backgroundColor: 'rgba(255,255,255,0.15)', borderRadius: 8, padding: '8px 14px', textAlign: 'center' }}>
                <p style={{ fontSize: 18, fontWeight: 800, margin: 0 }}>{profile.isOpen !== false ? '🟢' : '🔴'}</p>
                <p style={{ fontSize: 11, opacity: 0.85, margin: 0 }}>{profile.isOpen !== false ? 'Open' : 'Closed'}</p>
              </div>
            </>
          )}
          {type === 'rider' && (
            <>
              <div style={{ backgroundColor: 'rgba(255,255,255,0.15)', borderRadius: 8, padding: '8px 14px', textAlign: 'center' }}>
                <p style={{ fontSize: 18, fontWeight: 800, margin: 0 }}>{profile.totalDeliveries || 0}</p>
                <p style={{ fontSize: 11, opacity: 0.85, margin: 0 }}>Deliveries</p>
              </div>
              <div style={{ backgroundColor: 'rgba(255,255,255,0.15)', borderRadius: 8, padding: '8px 14px', textAlign: 'center' }}>
                <p style={{ fontSize: 18, fontWeight: 800, margin: 0 }}>GH₵ {(profile.totalEarnings || 0).toFixed(0)}</p>
                <p style={{ fontSize: 11, opacity: 0.85, margin: 0 }}>Earned</p>
              </div>
              <div style={{ backgroundColor: 'rgba(255,255,255,0.15)', borderRadius: 8, padding: '8px 14px', textAlign: 'center' }}>
                <p style={{ fontSize: 18, fontWeight: 800, margin: 0 }}>{profile.isOnline ? '🟢' : '⚫'}</p>
                <p style={{ fontSize: 11, opacity: 0.85, margin: 0 }}>{profile.isOnline ? 'Online' : 'Offline'}</p>
              </div>
            </>
          )}
        </div>
      </div>

      {/* TABS */}
      <div style={{ display: 'flex', borderBottom: '1px solid #E5E7EB', backgroundColor: '#FFF' }}>
        {TABS.map(tab => (
          <button key={tab} style={{ flex: 1, padding: '12px', border: 'none', cursor: 'pointer', fontSize: 13, fontWeight: activeTab === tab ? 700 : 400, backgroundColor: 'transparent', color: activeTab === tab ? '#1D9E75' : '#888', borderBottom: activeTab === tab ? '2px solid #1D9E75' : '2px solid transparent' }}
            onClick={() => setActiveTab(tab)}>
            {tab === 'overview' ? '📋 Overview' : tab === 'orders' ? `📦 Orders (${myOrders.length})` : `📝 Notes (${notes.length})`}
          </button>
        ))}
      </div>

      {/* CONTENT */}
      <div style={{ flex: 1, overflowY: 'auto', padding: 20 }}>

        {/* OVERVIEW TAB */}
        {activeTab === 'overview' && (
          <div>
            <div style={{ backgroundColor: '#F7F8FA', borderRadius: 10, padding: 16, marginBottom: 16 }}>
              <p style={{ fontWeight: 700, fontSize: 14, margin: '0 0 12px' }}>Profile Details</p>
              {type === 'customer' && (
                <table style={{ width: '100%', fontSize: 13 }}>
                  {[
                    ['📧 Email', profile.email],
                    ['📞 Phone', profile.phone || '—'],
                    ['🎂 Joined', profile.createdAt ? new Date(profile.createdAt).toLocaleDateString() : '—'],
                    ['📦 Last order', lastOrder ? new Date(lastOrder.createdAt).toLocaleDateString() : 'Never'],
                    ['💰 Avg order value', myOrders.length > 0 ? `GH₵ ${(totalSpent / myOrders.length).toFixed(2)}` : '—'],
                    ['✅ Delivered', delivered],
                    ['✕ Cancelled', cancelled],
                  ].map(([l, v]) => (
                    <tr key={l}><td style={{ padding: '5px 0', color: '#888', width: 140 }}>{l}</td><td style={{ padding: '5px 0', fontWeight: 500 }}>{v}</td></tr>
                  ))}
                </table>
              )}
              {type === 'pharmacy' && (
                <table style={{ width: '100%', fontSize: 13 }}>
                  {[
                    ['💊 Pharmacy', profile.pharmacyName],
                    ['👤 Owner', profile.ownerName || '—'],
                    ['📞 Phone', profile.phone || '—'],
                    ['📧 Email', profile.email || '—'],
                    ['📍 Location', profile.location || '—'],
                    ['🗺️ Region', profile.region || '—'],
                    ['🪪 License', profile.licenseNumber || '—'],
                    ['📅 Joined', profile.createdAt ? new Date(profile.createdAt).toLocaleDateString() : '—'],
                    ['✅ Status', profile.status],
                    ['🚫 Suspended', profile.suspended ? 'Yes' : 'No'],
                    ['📦 Orders fulfilled', delivered],
                    ['✕ Cancelled', cancelled],
                  ].map(([l, v]) => (
                    <tr key={l}><td style={{ padding: '5px 0', color: '#888', width: 140 }}>{l}</td><td style={{ padding: '5px 0', fontWeight: 500 }}>{String(v)}</td></tr>
                  ))}
                </table>
              )}
              {type === 'rider' && (
                <table style={{ width: '100%', fontSize: 13 }}>
                  {[
                    ['👤 Name', profile.name || '—'],
                    ['📞 Phone', profile.phone || '—'],
                    ['📧 Email', profile.email || '—'],
                    ['📅 Joined', profile.createdAt ? new Date(profile.createdAt).toLocaleDateString() : '—'],
                    ['📦 Total Deliveries', profile.totalDeliveries || 0],
                    ['💰 Total Earnings', `GH₵ ${(profile.totalEarnings || 0).toFixed(2)}`],
                    ['⭐ Rating', profile.rating ? `${profile.rating}/5` : '—'],
                    ['🟢 Online', profile.isOnline ? 'Yes' : 'No'],
                    ['✅ Approved', profile.isApproved ? 'Yes' : 'No'],
                    ['🚫 Suspended', profile.suspended ? 'Yes' : 'No'],
                  ].map(([l, v]) => (
                    <tr key={l}><td style={{ padding: '5px 0', color: '#888', width: 140 }}>{l}</td><td style={{ padding: '5px 0', fontWeight: 500 }}>{String(v)}</td></tr>
                  ))}
                </table>
              )}
            </div>

            {/* CUSTOMER SEGMENT */}
            {type === 'customer' && (
              <div style={{ backgroundColor: '#E8F8F2', borderRadius: 10, padding: 14, border: '1px solid #B2DFD0' }}>
                <p style={{ fontWeight: 700, fontSize: 13, color: '#1D6B52', margin: '0 0 8px' }}>🏷️ Customer Segment</p>
                <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                  {myOrders.length === 0 && <Badge text="🆕 New" bg="#EFF6FF" color="#1D4ED8" />}
                  {myOrders.length >= 1 && myOrders.length < 5 && <Badge text="🌱 Growing" bg="#FEF9C3" color="#92400E" />}
                  {myOrders.length >= 5 && <Badge text="⭐ Loyal" bg="#DCFCE7" color="#15803D" />}
                  {totalSpent >= 200 && <Badge text="💎 High Value" bg="#EDE9FE" color="#5B21B6" />}
                  {cancelled > 2 && <Badge text="⚠️ High Cancellation" bg="#FEE2E2" color="#B91C1C" />}
                  {lastOrder && new Date(lastOrder.createdAt) < new Date(Date.now() - 30 * 24 * 60 * 60 * 1000) && <Badge text="😴 Inactive 30d" bg="#F3F4F6" color="#6B7280" />}
                </div>
              </div>
            )}
          </div>
        )}

        {/* ORDERS TAB */}
        {activeTab === 'orders' && (
          <div>
            {myOrders.length === 0 ? (
              <div style={{ textAlign: 'center', padding: 40 }}>
                <p style={{ fontSize: 32, marginBottom: 10 }}>📦</p>
                <p style={{ color: '#888' }}>No orders yet</p>
              </div>
            ) : (
              myOrders.sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt)).map((order, i) => (
                <div key={order.id} style={{ backgroundColor: '#F7F8FA', borderRadius: 10, padding: 14, marginBottom: 10, border: '1px solid #E5E7EB' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 6 }}>
                    <span style={{ fontWeight: 700, color: '#1D9E75', fontSize: 13 }}>#{(order.orderId || order.id).slice(-6).toUpperCase()}</span>
                    <Badge
                      text={order.status}
                      bg={order.status === 'delivered' ? '#DCFCE7' : order.status === 'cancelled' ? '#FEE2E2' : '#FEF3C7'}
                      color={order.status === 'delivered' ? '#15803D' : order.status === 'cancelled' ? '#B91C1C' : '#92400E'}
                    />
                  </div>
                  <p style={{ fontSize: 12, color: '#555', margin: '0 0 4px' }}>💊 {order.items?.map(i => i.name).join(', ')}</p>
                  <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12, color: '#888' }}>
                    <span>{new Date(order.createdAt).toLocaleString()}</span>
                    <span style={{ fontWeight: 700, color: '#1D9E75' }}>GH₵ {(order.total || 0).toFixed(2)}</span>
                  </div>
                </div>
              ))
            )}
          </div>
        )}

        {/* NOTES TAB */}
        {activeTab === 'notes' && (
          <div>
            <div style={{ marginBottom: 16 }}>
              <textarea
                style={{ width: '100%', padding: '10px 14px', borderRadius: 10, border: '1px solid #E5E7EB', fontSize: 13, resize: 'vertical', minHeight: 80, fontFamily: 'inherit', outline: 'none', boxSizing: 'border-box', backgroundColor: '#F7F8FA' }}
                placeholder={`Add a note about this ${type}...`}
                value={noteText}
                onChange={e => setNoteText(e.target.value)}
              />
              <button style={{ ...s.btn, marginTop: 8, opacity: savingNote || !noteText.trim() ? 0.6 : 1 }} onClick={saveNote} disabled={savingNote || !noteText.trim()}>
                {savingNote ? 'Saving...' : '+ Add Note'}
              </button>
            </div>
            {notes.length === 0 ? (
              <div style={{ textAlign: 'center', padding: 30, color: '#888' }}>
                <p style={{ fontSize: 28, marginBottom: 8 }}>📝</p>
                <p>No notes yet. Add the first note above.</p>
              </div>
            ) : notes.map(note => (
              <div key={note.id} style={{ backgroundColor: '#FFFBEB', borderRadius: 10, padding: 14, marginBottom: 10, border: '1px solid #FCD34D' }}>
                <p style={{ fontSize: 13, color: '#1A1A1A', margin: '0 0 8px', lineHeight: 1.6 }}>{note.text}</p>
                <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 11, color: '#888' }}>
                  <span>👤 {note.addedBy}</span>
                  <span>{new Date(note.createdAt).toLocaleString()}</span>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

// ── BROADCAST MODAL ───────────────────────────────────────────────────────────
function BroadcastModal({ onClose, customers, pharmacies, riders }) {
  const [target, setTarget]     = useState('all_customers');
  const [title, setTitle]       = useState('');
  const [message, setMessage]   = useState('');
  const [sending, setSending]   = useState(false);
  const [sent, setSent]         = useState(false);

  const TARGETS = [
    { id: 'all_customers',  label: '👤 All Customers',         count: customers.length },
    { id: 'active_customers', label: '⭐ Active Customers (5+ orders)', count: customers.filter(c => c.orderCount >= 5).length },
    { id: 'inactive_customers', label: '😴 Inactive Customers (30d+)', count: customers.filter(c => c.daysSinceLastOrder > 30).length },
    { id: 'all_pharmacies', label: '💊 All Pharmacies',        count: pharmacies.filter(p => p.status === 'approved').length },
    { id: 'all_riders',     label: '🛵 All Riders',            count: riders.filter(r => r.isApproved).length },
  ];

  const handleSend = async () => {
    if (!title.trim() || !message.trim()) { alert('Enter a title and message'); return; }
    setSending(true);
    try {
      const broadcastRef = push(ref(db, 'broadcasts'));
      await set(broadcastRef, {
        title: title.trim(),
        message: message.trim(),
        target,
        sentAt: new Date().toISOString(),
        sentBy: auth.currentUser?.email,
        recipientCount: TARGETS.find(t => t.id === target)?.count || 0,
      });
      setSent(true);
    } catch (e) { alert('Error sending broadcast'); }
    finally { setSending(false); }
  };

  return (
    <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(0,0,0,0.6)', zIndex: 9999, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20 }}>
      <div style={{ backgroundColor: '#FFF', borderRadius: 16, padding: 28, maxWidth: 520, width: '100%' }}>
        {sent ? (
          <div style={{ textAlign: 'center', padding: '20px 0' }}>
            <p style={{ fontSize: 52, marginBottom: 12 }}>✅</p>
            <h3 style={{ fontWeight: 700, fontSize: 18, margin: '0 0 8px' }}>Broadcast Queued!</h3>
            <p style={{ color: '#888', fontSize: 14, margin: '0 0 20px' }}>
              {target === 'all_pharmacies'
                ? "Pharmacies don't have push notifications yet — this will show in their dashboard when they check Announcements."
                : `Sending as a real push notification to up to ${TARGETS.find(t => t.id === target)?.count || 0} recipients now. Check the Broadcast History table for actual delivery counts in a few seconds.`}
            </p>
            <button style={s.btn} onClick={onClose}>Done</button>
          </div>
        ) : (
          <>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
              <h3 style={{ fontWeight: 700, margin: 0, fontSize: 17 }}>📣 Send Broadcast Message</h3>
              <button style={{ background: 'none', border: 'none', fontSize: 20, cursor: 'pointer', color: '#888' }} onClick={onClose}>✕</button>
            </div>

            <div style={{ marginBottom: 14 }}>
              <label style={{ display: 'block', fontSize: 13, fontWeight: 600, marginBottom: 6, color: '#444' }}>Send to</label>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                {TARGETS.map(t => (
                  <label key={t.id} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '10px 14px', borderRadius: 8, border: `1.5px solid ${target === t.id ? '#1D9E75' : '#E5E7EB'}`, backgroundColor: target === t.id ? '#E8F8F2' : '#FFF', cursor: 'pointer' }}>
                    <input type="radio" value={t.id} checked={target === t.id} onChange={() => setTarget(t.id)} style={{ accentColor: '#1D9E75' }} />
                    <span style={{ flex: 1, fontSize: 13, fontWeight: 500 }}>{t.label}</span>
                    <span style={{ fontSize: 12, color: '#1D9E75', fontWeight: 700, backgroundColor: '#E8F8F2', padding: '2px 8px', borderRadius: 20 }}>{t.count}</span>
                  </label>
                ))}
              </div>
            </div>

            <div style={{ marginBottom: 14 }}>
              <label style={{ display: 'block', fontSize: 13, fontWeight: 600, marginBottom: 6, color: '#444' }}>Notification Title</label>
              <input style={{ ...s.input, width: '100%', boxSizing: 'border-box' }} placeholder="e.g. 🎉 Special offer for PharmDev customers" value={title} onChange={e => setTitle(e.target.value)} />
            </div>

            <div style={{ marginBottom: 20 }}>
              <label style={{ display: 'block', fontSize: 13, fontWeight: 600, marginBottom: 6, color: '#444' }}>Message</label>
              <textarea style={{ ...s.input, width: '100%', boxSizing: 'border-box', minHeight: 100, resize: 'vertical', fontFamily: 'inherit' }} placeholder="Type your message here..." value={message} onChange={e => setMessage(e.target.value)} />
              <p style={{ fontSize: 11, color: '#888', margin: '4px 0 0' }}>{message.length}/200 characters</p>
            </div>

            <div style={{ display: 'flex', gap: 10 }}>
              <button style={{ flex: 1, padding: '11px', border: '1px solid #E5E7EB', borderRadius: 10, cursor: 'pointer', backgroundColor: '#F7F8FA', color: '#555' }} onClick={onClose}>Cancel</button>
              <button style={{ flex: 2, ...s.btn, padding: '11px', borderRadius: 10, opacity: sending ? 0.6 : 1 }} onClick={handleSend} disabled={sending}>
                {sending ? 'Sending...' : `📣 Send to ${TARGETS.find(t => t.id === target)?.count || 0} recipients`}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

// ── MAIN CRM TAB ──────────────────────────────────────────────────────────────
// orders/pharmacies/riders/users are passed down from App.js, which already
// holds one live subscription to each — re-subscribing here would mean the
// whole system-wide orders/users trees get downloaded a second time, every
// time this tab mounts, on top of App.js's own copy.
export default function CRMTab({ orders = [], pharmacies = [], riders = [], users: customers = [] }) {
  const [activeView, setActiveView]   = useState('customers');
  const [broadcasts, setBroadcasts]   = useState([]);
  const [search, setSearch]           = useState('');
  const [segmentFilter, setSegmentFilter] = useState('all');
  const [selectedProfile, setSelectedProfile] = useState(null);
  const [selectedType, setSelectedType]       = useState(null);
  const [showBroadcast, setShowBroadcast]     = useState(false);

  useEffect(() => {
    return onValue(ref(db, 'broadcasts'), snap => {
      setBroadcasts(snap.exists() ? Object.entries(snap.val()).map(([k, v]) => ({ id: k, ...v })).sort((a, b) => new Date(b.sentAt) - new Date(a.sentAt)) : []);
    });
  }, []);

  // Enrich customers with order stats
  const enrichedCustomers = customers.map(c => {
    const myOrders = orders.filter(o => o.customerId === c.uid || o.customerId === c.id);
    const delivered = myOrders.filter(o => o.status === 'delivered');
    const lastOrder = myOrders.sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt))[0];
    const daysSinceLastOrder = lastOrder ? Math.floor((Date.now() - new Date(lastOrder.createdAt)) / (1000 * 60 * 60 * 24)) : 999;
    return {
      ...c,
      orderCount: myOrders.length,
      totalSpent: delivered.reduce((s, o) => s + (o.total || 0), 0),
      lastOrderDate: lastOrder?.createdAt || null,
      daysSinceLastOrder,
    };
  });

  const getSegment = (c) => {
    if (c.orderCount === 0) return 'new';
    if (c.totalSpent >= 200) return 'highvalue';
    if (c.daysSinceLastOrder > 30) return 'inactive';
    if (c.orderCount >= 5) return 'loyal';
    return 'growing';
  };

  const SEGMENTS = [
    { id: 'all',       label: 'All',          color: '#555' },
    { id: 'new',       label: '🆕 New',        color: '#1D4ED8' },
    { id: 'growing',   label: '🌱 Growing',    color: '#92400E' },
    { id: 'loyal',     label: '⭐ Loyal',      color: '#15803D' },
    { id: 'highvalue', label: '💎 High Value', color: '#5B21B6' },
    { id: 'inactive',  label: '😴 Inactive',   color: '#6B7280' },
  ];

  const filteredCustomers = enrichedCustomers.filter(c => {
    const matchSearch = !search ||
      (c.name || c.displayName || '').toLowerCase().includes(search.toLowerCase()) ||
      (c.email || '').toLowerCase().includes(search.toLowerCase()) ||
      (c.phone || '').includes(search);
    const matchSegment = segmentFilter === 'all' || getSegment(c) === segmentFilter;
    return matchSearch && matchSegment;
  });

  const filteredPharmacies = pharmacies.filter(p =>
    !search ||
    (p.pharmacyName || '').toLowerCase().includes(search.toLowerCase()) ||
    (p.ownerName || '').toLowerCase().includes(search.toLowerCase()) ||
    (p.region || '').toLowerCase().includes(search.toLowerCase()) ||
    (p.phone || '').includes(search)
  );

  const filteredRiders = riders.filter(r =>
    !search ||
    (r.name || '').toLowerCase().includes(search.toLowerCase()) ||
    (r.phone || '').includes(search) ||
    (r.email || '').toLowerCase().includes(search.toLowerCase())
  );

  const VIEWS = [
    { id: 'customers',  label: `👤 Customers (${customers.length})` },
    { id: 'pharmacies', label: `💊 Pharmacies (${pharmacies.length})` },
    { id: 'riders',     label: `🛵 Riders (${riders.length})` },
    { id: 'broadcast',  label: `📣 Broadcasts (${broadcasts.length})` },
  ];

  return (
    <div>
      {selectedProfile && (
        <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(0,0,0,0.3)', zIndex: 9998 }} onClick={() => setSelectedProfile(null)} />
      )}
      {selectedProfile && (
        <ProfileDrawer profile={selectedProfile} type={selectedType} orders={orders} onClose={() => setSelectedProfile(null)} />
      )}
      {showBroadcast && (
        <BroadcastModal onClose={() => setShowBroadcast(false)} customers={enrichedCustomers} pharmacies={pharmacies} riders={riders} />
      )}

      {/* HEADER */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
        <div>
          <h2 style={{ fontWeight: 700, margin: 0, fontSize: 20 }}>👥 CRM — Customer Relationship Manager</h2>
          <p style={{ color: '#888', fontSize: 13, margin: '4px 0 0' }}>Manage customers, pharmacies, riders and send broadcast messages</p>
        </div>
        <button style={{ ...s.btn, backgroundColor: '#7C3AED', display: 'flex', alignItems: 'center', gap: 8 }} onClick={() => setShowBroadcast(true)}>
          📣 Send Broadcast
        </button>
      </div>

      {/* CRM STATS */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(5, 1fr)', gap: 12, marginBottom: 20 }}>
        {[
          { label: 'Total Customers', value: customers.length, color: '#F59E0B', icon: '👤' },
          { label: 'Loyal (5+ orders)', value: enrichedCustomers.filter(c => c.orderCount >= 5).length, color: '#1D9E75', icon: '⭐' },
          { label: 'High Value (GH₵200+)', value: enrichedCustomers.filter(c => c.totalSpent >= 200).length, color: '#7C3AED', icon: '💎' },
          { label: 'Inactive (30d+)', value: enrichedCustomers.filter(c => c.daysSinceLastOrder > 30 && c.orderCount > 0).length, color: '#6B7280', icon: '😴' },
          { label: 'Broadcasts Sent', value: broadcasts.length, color: '#3B82F6', icon: '📣' },
        ].map((st, i) => (
          <div key={i} style={{ backgroundColor: '#FFF', borderRadius: 10, padding: 14, border: '1px solid #E5E7EB', borderTop: `3px solid ${st.color}`, textAlign: 'center' }}>
            <p style={{ fontSize: 20, margin: '0 0 6px' }}>{st.icon}</p>
            <p style={{ fontSize: 22, fontWeight: 800, color: st.color, margin: '0 0 3px' }}>{st.value}</p>
            <p style={{ fontSize: 11, color: '#888', margin: 0, lineHeight: 1.4 }}>{st.label}</p>
          </div>
        ))}
      </div>

      {/* VIEW TABS */}
      <div style={{ display: 'flex', gap: 4, marginBottom: 16, backgroundColor: '#F1F3F5', borderRadius: 10, padding: 4, width: 'fit-content' }}>
        {VIEWS.map(v => (
          <button key={v.id} style={{ padding: '8px 16px', borderRadius: 8, border: 'none', cursor: 'pointer', fontSize: 13, fontWeight: activeView === v.id ? 700 : 400, backgroundColor: activeView === v.id ? '#FFF' : 'transparent', color: activeView === v.id ? '#1D9E75' : '#888' }}
            onClick={() => { setActiveView(v.id); setSearch(''); setSegmentFilter('all'); }}>
            {v.label}
          </button>
        ))}
      </div>

      {/* SEARCH + FILTER */}
      {activeView !== 'broadcast' && (
        <div style={{ display: 'flex', gap: 10, marginBottom: 16, flexWrap: 'wrap', alignItems: 'center' }}>
          <input style={{ ...s.input, width: 280 }} placeholder={`Search ${activeView}...`} value={search} onChange={e => setSearch(e.target.value)} />
          {activeView === 'customers' && (
            <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
              {SEGMENTS.map(seg => (
                <button key={seg.id} style={{ padding: '6px 12px', borderRadius: 20, border: `1px solid ${segmentFilter === seg.id ? seg.color : '#E5E7EB'}`, backgroundColor: segmentFilter === seg.id ? seg.color + '15' : '#FFF', color: segmentFilter === seg.id ? seg.color : '#888', cursor: 'pointer', fontSize: 12, fontWeight: segmentFilter === seg.id ? 700 : 400 }}
                  onClick={() => setSegmentFilter(seg.id)}>
                  {seg.label}
                </button>
              ))}
            </div>
          )}
          {search && (
            <button style={{ padding: '6px 12px', backgroundColor: '#FEE2E2', color: '#B91C1C', border: 'none', borderRadius: 8, cursor: 'pointer', fontSize: 12 }} onClick={() => setSearch('')}>✕ Clear</button>
          )}
        </div>
      )}

      {/* ── CUSTOMERS ── */}
      {activeView === 'customers' && (
        filteredCustomers.length === 0 ? (
          <div style={{ ...s.card, textAlign: 'center', padding: 50 }}><p style={{ fontSize: 32 }}>👤</p><p style={{ color: '#888' }}>No customers found</p></div>
        ) : (
          <div style={{ ...s.card }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
              <thead>
                <tr>
                  {['Customer', 'Phone', 'Orders', 'Total Spent', 'Last Order', 'Segment', 'Actions'].map(h => (
                    <th key={h} style={s.th}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {filteredCustomers.map((c, i) => {
                  const seg = getSegment(c);
                  const SEG_STYLE = {
                    new:       { bg: '#EFF6FF', color: '#1D4ED8', label: '🆕 New' },
                    growing:   { bg: '#FEF9C3', color: '#92400E', label: '🌱 Growing' },
                    loyal:     { bg: '#DCFCE7', color: '#15803D', label: '⭐ Loyal' },
                    highvalue: { bg: '#EDE9FE', color: '#5B21B6', label: '💎 High Value' },
                    inactive:  { bg: '#F3F4F6', color: '#6B7280', label: '😴 Inactive' },
                  };
                  const ss = SEG_STYLE[seg] || SEG_STYLE.new;
                  return (
                    <tr key={c.id} style={{ backgroundColor: i % 2 === 0 ? '#FFF' : '#FAFAFA', cursor: 'pointer' }} onClick={() => { setSelectedProfile(c); setSelectedType('customer'); }}>
                      <td style={s.td}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                          <div style={{ width: 34, height: 34, borderRadius: 17, backgroundColor: '#FEF3C7', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 700, color: '#92400E', fontSize: 14, flexShrink: 0 }}>
                            {(c.name || c.displayName || c.email || 'C').charAt(0).toUpperCase()}
                          </div>
                          <div>
                            <p style={{ fontWeight: 600, margin: 0, color: '#1A1A1A' }}>{c.name || c.displayName || c.email?.split('@')[0] || 'Customer'}</p>
                            <p style={{ fontSize: 11, color: '#888', margin: 0 }}>{c.email}</p>
                          </div>
                        </div>
                      </td>
                      <td style={s.td}>{c.phone || '—'}</td>
                      <td style={{ ...s.td, fontWeight: 700, color: '#1D9E75' }}>{c.orderCount}</td>
                      <td style={{ ...s.td, fontWeight: 700, color: '#7C3AED' }}>GH₵ {c.totalSpent.toFixed(2)}</td>
                      <td style={{ ...s.td, color: '#888', fontSize: 12 }}>{c.lastOrderDate ? new Date(c.lastOrderDate).toLocaleDateString() : 'Never'}</td>
                      <td style={s.td}><Badge text={ss.label} bg={ss.bg} color={ss.color} /></td>
                      <td style={s.td}>
                        <button style={{ ...s.btn, padding: '5px 12px', fontSize: 12 }} onClick={e => { e.stopPropagation(); setSelectedProfile(c); setSelectedType('customer'); }}>
                          View →
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )
      )}

      {/* ── PHARMACIES ── */}
      {activeView === 'pharmacies' && (
        filteredPharmacies.length === 0 ? (
          <div style={{ ...s.card, textAlign: 'center', padding: 50 }}><p style={{ fontSize: 32 }}>💊</p><p style={{ color: '#888' }}>No pharmacies found</p></div>
        ) : (
          <div style={s.card}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
              <thead>
                <tr>{['Pharmacy', 'Owner', 'Region', 'Phone', 'Status', 'Orders', 'Actions'].map(h => <th key={h} style={s.th}>{h}</th>)}</tr>
              </thead>
              <tbody>
                {filteredPharmacies.map((p, i) => {
                  const myOrders = orders.filter(o => o.items?.[0]?.pharmacyId === p.id || o.items?.[0]?.pharmacy === p.pharmacyName);
                  return (
                    <tr key={p.id} style={{ backgroundColor: i % 2 === 0 ? '#FFF' : '#FAFAFA', cursor: 'pointer' }} onClick={() => { setSelectedProfile(p); setSelectedType('pharmacy'); }}>
                      <td style={s.td}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                          <div style={{ width: 34, height: 34, borderRadius: 17, backgroundColor: '#E8F8F2', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 18, flexShrink: 0 }}>💊</div>
                          <div>
                            <p style={{ fontWeight: 600, margin: 0, color: '#1A1A1A' }}>{p.pharmacyName}</p>
                            <p style={{ fontSize: 11, color: '#888', margin: 0 }}>{p.email}</p>
                          </div>
                        </div>
                      </td>
                      <td style={s.td}>{p.ownerName || '—'}</td>
                      <td style={s.td}>{p.region || '—'}</td>
                      <td style={s.td}>{p.phone || '—'}</td>
                      <td style={s.td}>
                        <Badge
                          text={p.suspended ? '🚫 Suspended' : p.status === 'approved' ? '✅ Active' : '⏳ Pending'}
                          bg={p.suspended ? '#FEE2E2' : p.status === 'approved' ? '#DCFCE7' : '#FEF3C7'}
                          color={p.suspended ? '#B91C1C' : p.status === 'approved' ? '#15803D' : '#92400E'}
                        />
                      </td>
                      <td style={{ ...s.td, fontWeight: 700, color: '#1D9E75' }}>{myOrders.length}</td>
                      <td style={s.td}>
                        <button style={{ ...s.btn, padding: '5px 12px', fontSize: 12 }} onClick={e => { e.stopPropagation(); setSelectedProfile(p); setSelectedType('pharmacy'); }}>
                          View →
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )
      )}

      {/* ── RIDERS ── */}
      {activeView === 'riders' && (
        filteredRiders.length === 0 ? (
          <div style={{ ...s.card, textAlign: 'center', padding: 50 }}><p style={{ fontSize: 32 }}>🛵</p><p style={{ color: '#888' }}>No riders found</p></div>
        ) : (
          <div style={s.card}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
              <thead>
                <tr>{['Rider', 'Phone', 'Deliveries', 'Earnings', 'Status', 'Online', 'Actions'].map(h => <th key={h} style={s.th}>{h}</th>)}</tr>
              </thead>
              <tbody>
                {filteredRiders.map((r, i) => (
                  <tr key={r.id} style={{ backgroundColor: i % 2 === 0 ? '#FFF' : '#FAFAFA', cursor: 'pointer' }} onClick={() => { setSelectedProfile(r); setSelectedType('rider'); }}>
                    <td style={s.td}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                        <div style={{ width: 34, height: 34, borderRadius: 17, backgroundColor: '#EFF6FF', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 18, flexShrink: 0 }}>🛵</div>
                        <div>
                          <p style={{ fontWeight: 600, margin: 0, color: '#1A1A1A' }}>{r.name || 'Rider'}</p>
                          <p style={{ fontSize: 11, color: '#888', margin: 0 }}>{r.email}</p>
                        </div>
                      </div>
                    </td>
                    <td style={s.td}>{r.phone || '—'}</td>
                    <td style={{ ...s.td, fontWeight: 700, color: '#3B82F6' }}>{r.totalDeliveries || 0}</td>
                    <td style={{ ...s.td, fontWeight: 700, color: '#1D9E75' }}>GH₵ {(r.totalEarnings || 0).toFixed(2)}</td>
                    <td style={s.td}>
                      <Badge
                        text={r.suspended ? '🚫 Suspended' : r.isApproved ? '✅ Active' : '⏳ Pending'}
                        bg={r.suspended ? '#FEE2E2' : r.isApproved ? '#DCFCE7' : '#FEF3C7'}
                        color={r.suspended ? '#B91C1C' : r.isApproved ? '#15803D' : '#92400E'}
                      />
                    </td>
                    <td style={s.td}>
                      <Badge text={r.isOnline ? '🟢 Online' : '⚫ Offline'} bg={r.isOnline ? '#DCFCE7' : '#F3F4F6'} color={r.isOnline ? '#15803D' : '#6B7280'} />
                    </td>
                    <td style={s.td}>
                      <button style={{ ...s.btn, padding: '5px 12px', fontSize: 12 }} onClick={e => { e.stopPropagation(); setSelectedProfile(r); setSelectedType('rider'); }}>
                        View →
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )
      )}

      {/* ── BROADCASTS ── */}
      {activeView === 'broadcast' && (
        <div>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
            <p style={{ fontWeight: 700, fontSize: 15, margin: 0 }}>📣 Broadcast History ({broadcasts.length})</p>
            <button style={{ ...s.btn, backgroundColor: '#7C3AED' }} onClick={() => setShowBroadcast(true)}>+ New Broadcast</button>
          </div>
          {broadcasts.length === 0 ? (
            <div style={{ ...s.card, textAlign: 'center', padding: 50 }}>
              <p style={{ fontSize: 40, marginBottom: 12 }}>📣</p>
              <p style={{ fontWeight: 600, color: '#333', margin: '0 0 6px' }}>No broadcasts sent yet</p>
              <p style={{ fontSize: 13, color: '#888', margin: '0 0 20px' }}>Send your first message to customers, pharmacies or riders</p>
              <button style={{ ...s.btn, backgroundColor: '#7C3AED' }} onClick={() => setShowBroadcast(true)}>Send First Broadcast →</button>
            </div>
          ) : (
            <div style={s.card}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                <thead>
                  <tr>{['Title', 'Message', 'Sent To', 'Recipients', 'Delivery', 'Date', 'Sent By'].map(h => <th key={h} style={s.th}>{h}</th>)}</tr>
                </thead>
                <tbody>
                  {broadcasts.map((b, i) => (
                    <tr key={b.id} style={{ backgroundColor: i % 2 === 0 ? '#FFF' : '#FAFAFA' }}>
                      <td style={{ ...s.td, fontWeight: 600 }}>{b.title}</td>
                      <td style={{ ...s.td, maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', color: '#555' }}>{b.message}</td>
                      <td style={s.td}>
                        <Badge
                          text={b.target === 'all_customers' ? '👤 All Customers' : b.target === 'all_pharmacies' ? '💊 All Pharmacies' : b.target === 'all_riders' ? '🛵 All Riders' : b.target === 'active_customers' ? '⭐ Active' : '😴 Inactive'}
                          bg="#E8F8F2" color="#1D6B52"
                        />
                      </td>
                      <td style={{ ...s.td, fontWeight: 700, color: '#7C3AED' }}>{b.recipientCount}</td>
                      <td style={s.td}>
                        {!b.deliveryResult ? (
                          <Badge text="⏳ Sending..." bg="#FEF3C7" color="#92400E" />
                        ) : b.deliveryResult.error ? (
                          <Badge text={`❌ Failed`} bg="#FEE2E2" color="#B91C1C" />
                        ) : b.target === 'all_pharmacies' ? (
                          <Badge text="📋 Dashboard only" bg="#EFF6FF" color="#1D4ED8" />
                        ) : (
                          <Badge text={`✅ Pushed to ${b.deliveryResult.pushSent}`} bg="#DCFCE7" color="#15803D" />
                        )}
                      </td>
                      <td style={{ ...s.td, fontSize: 12, color: '#888' }}>{new Date(b.sentAt).toLocaleString()}</td>
                      <td style={{ ...s.td, fontSize: 12, color: '#888' }}>{b.sentBy}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}
    </div>
  );
}